'use client'

import {
  ArrowRight,
  CheckmarkFilled as CheckCircle2,
} from '@carbon/icons-react'

import { EASE, HAIRLINE, SectionHead, useReveal } from '@/components/page-kit'
import { cn } from '@/lib/utils'

/* Six before/after pairs set as a ledger: one hairline-divided grid, each cell
   carrying its record number, the pain quote, and the resolution under a rule.
   The old build floated dark slate cards on a light gradient band — unreadable
   the moment the theme flipped. Everything here rides the tokens instead. */

const transformationData = [
  {
    before: `"Fee collection is scattered across spreadsheets, paper receipts, and WhatsApp messages."`,
    after:
      'Automated online collection (UPI, NEFT, card) with instant digital receipts and automated parent SMS.',
  },
  {
    before: `"Admission season means piles of paper forms, missing documents, and frantic follow-up calls."`,
    after:
      'Digital application forms, automated document checklists, and a live pipeline your team can track in real time.',
  },
  {
    before: `"Parents call the office all day asking for attendance updates, fee balances, and exam results."`,
    after:
      'Parents see everything in real time through the parent portal. Zero calls, complete transparency.',
  },
  {
    before: `"Staff payroll takes our accounts team three full days each month to calculate and process."`,
    after:
      'Payroll computes automatically from attendance and leave data, processed and slips sent in under an hour.',
  },
  {
    before: `"We use six different software tools that don't share data with each other."`,
    after:
      'One unified platform. Student records, staff data, finance, and academics, always perfectly in sync.',
  },
  {
    before: `"Our current vendor charges per user and we can't export our own school's data."`,
    after:
      'Flexible pricing with no per-user fees, along with complete data ownership and full portability.',
  },
]

/** Restores the em-dashes the copy lost to double spaces (page-kit has the
    same repair for products.ts strings). */
const dashes = (s: string) => s.replace(/ {2,}/g, ' — ')

function TransformationCell({
  before,
  after,
  index,
}: {
  before: string
  after: string
  index: number
}) {
  const { ref, shown } = useReveal<HTMLDivElement>(0.15)

  return (
    <div
      ref={ref}
      className="flex h-full flex-col bg-background p-6 sm:p-7 md:p-8"
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'translateY(0)' : 'translateY(20px)',
        transition: `opacity 0.6s ${EASE} ${index * 60}ms, transform 0.6s ${EASE} ${index * 60}ms`,
      }}
    >
      {/* Record head — status grammar kept from the source, set as text */}
      <div className="flex items-baseline justify-between gap-4">
        <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em]">
          <span className="text-rose-500/80 dark:text-rose-400/80">
            Pain Point
          </span>
          <ArrowRight aria-hidden className="size-3 text-muted-foreground/50" />
          <span className="text-emerald-600 dark:text-primary-active">
            Our Solution
          </span>
        </p>
        <span className="font-mono text-[11px] text-muted-foreground/60">
          0{index + 1}
        </span>
      </div>

      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
        {before}
      </p>

      <div
        className={cn(
          'mt-auto flex items-start gap-2.5 border-t pt-4',
          HAIRLINE,
          'mt-5'
        )}
      >
        <CheckCircle2
          aria-hidden
          className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-primary-active"
        />
        <p className="text-sm leading-relaxed text-foreground">
          {dashes(after)}
        </p>
      </div>
    </div>
  )
}

export function PainSection() {
  return (
    <section className="bg-background py-16 sm:py-20 md:py-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="Transformation Journey"
          title="From daily friction to seamless operations"
          deck="See how schools like yours are replacing legacy pain points with modern, efficient solutions."
        />

        {/* Ledger: 1px gaps over the hairline colour draw the rules, so the
            grid reads as one ruled surface rather than six floating cards. */}
        <div
          className={cn(
            'mt-10 overflow-hidden rounded-xl border sm:mt-12',
            HAIRLINE
          )}
        >
          <div className="grid gap-px bg-border/70 md:grid-cols-2 xl:grid-cols-3">
            {transformationData.map((item, idx) => (
              <TransformationCell
                key={idx}
                index={idx}
                before={item.before}
                after={item.after}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
